import * as React from 'react';

/**
 * Whether realtime sync is currently down, shared between the screens that observe a data sync
 * and the app layout that renders the banner.
 *
 * Reporters register themselves with a token so that several screens can observe different
 * content at the same time: sync is considered down as long as any one of them says so.
 */
const disconnectedTokens = new Set<object>();
const listeners = new Set<() => void>();

let snapshot = false;

/**
 * Report whether the content a reporter is watching is syncing. Pass `false` when it recovers,
 * and on unmount, so the reporter stops contributing.
 */
export function setRealtimeSyncDisconnected(token: object, disconnected: boolean): void {
    if (disconnected) {
        disconnectedTokens.add(token);
    } else {
        disconnectedTokens.delete(token);
    }

    const next = disconnectedTokens.size > 0;
    if (next === snapshot) {
        return;
    }

    snapshot = next;
    listeners.forEach((listener) => listener());
}

/**
 * Whether realtime sync is currently down for any observed content.
 */
export function useIsRealtimeSyncDisconnected(): boolean {
    return React.useSyncExternalStore(subscribeToRealtimeSync, getSnapshot, getSnapshot);
}

/**
 * Whether realtime sync is currently down for any observed content, outside of React.
 */
export function getRealtimeSyncDisconnected(): boolean {
    return snapshot;
}

/**
 * Be notified whenever the answer to {@link getRealtimeSyncDisconnected} changes.
 */
export function subscribeToRealtimeSync(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot(): boolean {
    return snapshot;
}
